import { useTranslation } from "react-i18next";
import { Card } from "@/components/ui/card";
import { motion } from "framer-motion";
import {
  Compass,
  Rocket,
  Building2,
  TrendingUp,
  Scale,
  Wallet,
  UserSquare2,
  ShoppingBag,
  Megaphone,
  Landmark,
  FolderLock,
} from "lucide-react";

const STAGES = [
  { icon: Compass, key: "stage1" },
  { icon: Building2, key: "stage2" },
  { icon: TrendingUp, key: "stage3" },
  { icon: Rocket, key: "stage4" },
];

export function JourneyStages() {
  const { t } = useTranslation();
  return (
    <section className="relative overflow-hidden mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10 sm:py-14" data-testid="how-it-works-section">
      <div className="pointer-events-none absolute inset-x-0 top-8 h-40 bg-[radial-gradient(circle_at_50%_0%,rgba(45,84,94,0.08),transparent_60%)]" aria-hidden />
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-40px" }}
        transition={{ duration: 0.5 }}
        className="relative text-center mb-8 sm:mb-10"
      >
        <span className="section-eyebrow justify-center">{t("landing.stagesEyebrow")}</span>
        <h2 className="font-display mt-3 text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight">{t("landing.stagesTitle")}</h2>
        <p className="mt-2 max-w-2xl mx-auto text-sm sm:text-base text-muted-foreground">{t("landing.stagesSubtitle")}</p>
      </motion.div>
      <motion.div
        className="relative flex sm:grid overflow-x-auto sm:overflow-visible snap-x snap-mandatory sm:grid-cols-2 lg:grid-cols-4 gap-4 pb-2 sm:pb-0 -mx-4 px-4 sm:mx-0 sm:px-0 [&::-webkit-scrollbar]:hidden [scrollbar-width:none]"
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, margin: "-60px" }}
        variants={{ show: { transition: { staggerChildren: 0.08 } } }}
      >
        {STAGES.map((s, i) => {
          const Icon = s.icon;
          return (
            <motion.div
              key={s.key}
              variants={{ hidden: { opacity: 0, x: 30 }, show: { opacity: 1, x: 0 } }}
              whileHover={{ y: -3 }}
              transition={{ duration: 0.45 }}
              className="w-[78%] sm:w-auto snap-center flex-shrink-0"
            >
              <Card className="relative rounded-2xl border border-border p-5 sm:p-6 h-full w-full card-elevate group overflow-hidden" data-testid={`hiw-card-${i}`}>
                <div className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-gradient-to-br from-primary/8 via-transparent to-secondary/10" aria-hidden />
                <span className="absolute end-5 top-5 text-xs font-mono text-muted-foreground">0{i + 1}</span>
                <div className="relative h-11 w-11 rounded-xl bg-primary/10 text-primary grid place-items-center group-hover:scale-110 group-hover:bg-primary/15 transition-transform duration-300">
                  <Icon className="h-5 w-5" />
                </div>
                <h3 className="relative font-display text-xl font-semibold mt-4">{t(`landing.${s.key}Title`)}</h3>
                <p className="relative text-sm text-muted-foreground mt-2 leading-relaxed">{t(`landing.${s.key}Desc`)}</p>
              </Card>
            </motion.div>
          );
        })}
      </motion.div>
    </section>
  );
}

const SERVICES = [
  { icon: Landmark, key: "svcMarket" },
  { icon: Wallet, key: "svcFinance" },
  { icon: Scale, key: "svcLegal" },
  { icon: UserSquare2, key: "svcHR" },
  { icon: ShoppingBag, key: "svcProcurement" },
  { icon: Megaphone, key: "svcMarketing" },
  { icon: TrendingUp, key: "svcAdvisory" },
  { icon: FolderLock, key: "svcDataRoom" },
];

export function ServicesGrid() {
  const { t } = useTranslation();
  return (
    <section id="services" className="relative overflow-hidden mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10 sm:py-14" data-testid="services-section">
      <motion.div
        aria-hidden
        className="pointer-events-none absolute -top-20 right-1/4 h-56 w-56 rounded-full bg-secondary/15 blur-3xl"
        animate={{ x: [0, 25, -10, 0], y: [0, 15, -8, 0] }}
        transition={{ duration: 26, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-40px" }}
        transition={{ duration: 0.5 }}
        className="relative flex items-end justify-between flex-wrap gap-4 mb-6 sm:mb-8"
      >
        <div>
          <span className="section-eyebrow">{t("landing.servicesEyebrow")}</span>
          <h2 className="font-display mt-2 text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight">{t("landing.servicesTitle")}</h2>
          <p className="mt-2 max-w-2xl text-sm sm:text-base text-muted-foreground">{t("landing.servicesSubtitle")}</p>
        </div>
      </motion.div>
      <motion.div
        className="relative flex sm:grid overflow-x-auto sm:overflow-visible snap-x snap-mandatory sm:grid-cols-2 lg:grid-cols-4 gap-4 pb-2 sm:pb-0 -mx-4 px-4 sm:mx-0 sm:px-0 [&::-webkit-scrollbar]:hidden [scrollbar-width:none]"
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, margin: "-60px" }}
        variants={{ show: { transition: { staggerChildren: 0.05 } } }}
      >
        {SERVICES.map((s, i) => {
          const Icon = s.icon;
          return (
            <motion.div
              key={s.key}
              variants={{ hidden: { opacity: 0, x: 30, scale: 0.98 }, show: { opacity: 1, x: 0, scale: 1 } }}
              whileHover={{ y: -4 }}
              transition={{ duration: 0.4 }}
              className="w-[70%] sm:w-auto snap-center flex-shrink-0"
            >
              <Card className="rounded-2xl border border-border p-5 card-elevate group h-full w-full relative overflow-hidden" data-testid={`service-card-${i}`}>
                <div className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-gradient-to-br from-primary/8 via-transparent to-secondary/10" aria-hidden />
                <div className="relative h-10 w-10 rounded-xl bg-primary/10 text-primary grid place-items-center mb-3 group-hover:rotate-6 group-hover:bg-primary/15 transition-transform duration-300">
                  <Icon className="h-5 w-5" />
                </div>
              <h3 className="relative font-semibold text-base">{t(`landing.${s.key}Title`)}</h3>
              <p className="relative text-sm text-muted-foreground mt-1.5 leading-relaxed">{t(`landing.${s.key}Desc`)}</p>
            </Card>
            </motion.div>
          );
        })}
      </motion.div>
    </section>
  );
}

const TESTIMONIALS = [
  { quoteKey: "landing.t1Quote", nameKey: "landing.t1Name", roleKey: "landing.t1Role" },
  { quoteKey: "landing.t2Quote", nameKey: "landing.t2Name", roleKey: "landing.t2Role" },
  { quoteKey: "landing.t3Quote", nameKey: "landing.t3Name", roleKey: "landing.t3Role" },
];

export function Testimonials() {
  const { t } = useTranslation();
  return (
    <section className="relative overflow-hidden mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10 sm:py-14" data-testid="testimonials-section">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-40px" }}
        transition={{ duration: 0.5 }}
        className="relative flex items-end justify-between mb-6 sm:mb-8 flex-wrap gap-4"
      >
        <div>
          <span className="section-eyebrow">{t("landing.testimonialsEyebrow")}</span>
          <h2 className="font-display mt-2 text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight">{t("landing.testimonialsTitle")}</h2>
        </div>
      </motion.div>
      <motion.div
        className="relative flex md:grid overflow-x-auto md:overflow-visible snap-x snap-mandatory md:grid-cols-3 gap-4 sm:gap-6 pb-2 md:pb-0 -mx-4 px-4 md:mx-0 md:px-0 [&::-webkit-scrollbar]:hidden [scrollbar-width:none]"
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, margin: "-60px" }}
        variants={{ show: { transition: { staggerChildren: 0.1 } } }}
      >
        {TESTIMONIALS.map((tt, i) => (
          <motion.div
            key={tt.quoteKey}
            variants={{ hidden: { opacity: 0, x: 40 }, show: { opacity: 1, x: 0 } }}
            whileHover={{ y: -4 }}
            transition={{ duration: 0.5 }}
            className="w-[82%] md:w-auto snap-center flex-shrink-0"
          >
            <Card className="rounded-2xl border border-border p-5 sm:p-6 card-elevate flex flex-col h-full w-full relative overflow-hidden group" data-testid={`testimonial-card-${i}`}>
              <div className="pointer-events-none absolute -top-6 -left-3 text-[80px] font-display leading-none text-primary/10 select-none" aria-hidden>“</div>
              <div className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-gradient-to-br from-primary/6 via-transparent to-secondary/10" aria-hidden />
              <p className="relative font-display text-lg leading-snug">“{t(tt.quoteKey)}”</p>
              <div className="relative mt-auto pt-6 flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-secondary/20 text-[color:var(--brand-secondary)] font-semibold grid place-items-center text-sm">
                  {t(tt.nameKey).split(" ").map((s) => s[0]).slice(0, 2).join("")}
                </div>
                <div>
                  <p className="text-sm font-medium">{t(tt.nameKey)}</p>
                  <p className="text-xs text-muted-foreground">{t(tt.roleKey)}</p>
                </div>
              </div>
            </Card>
          </motion.div>
        ))}
      </motion.div>
    </section>
  );
}

export function FinalCTA() {
  const { t } = useTranslation();
  return (
    <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10 sm:py-14" data-testid="final-cta-section">
      <motion.div
        initial={{ opacity: 0, y: 16, scale: 0.98 }}
        whileInView={{ opacity: 1, y: 0, scale: 1 }}
        viewport={{ once: true, margin: "-40px" }}
        transition={{ duration: 0.5 }}
      >
        <Card className="relative rounded-3xl border border-border overflow-hidden p-6 sm:p-10 text-center">
          <motion.div
            aria-hidden
            className="pointer-events-none absolute -top-24 -left-16 h-64 w-64 rounded-full bg-primary/20 blur-3xl"
            animate={{ x: [0, 25, -10, 0], y: [0, 20, -5, 0] }}
            transition={{ duration: 22, repeat: Infinity, ease: "easeInOut" }}
          />
          <motion.div
            aria-hidden
            className="pointer-events-none absolute -bottom-24 -right-16 h-64 w-64 rounded-full bg-secondary/25 blur-3xl"
            animate={{ x: [0, -20, 10, 0], y: [0, -15, 5, 0] }}
            transition={{ duration: 26, repeat: Infinity, ease: "easeInOut" }}
          />
          <div
            className="absolute inset-0 -z-0"
            style={{
              "background":
                "radial-gradient(500px circle at 30% 30%, rgba(45, 84, 94, 0.18), transparent 60%), radial-gradient(500px circle at 70% 70%, rgba(225, 179, 130, 0.20), transparent 60%)",
            }}
          />
          <div className="relative">
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight">{t("landing.finalCtaTitle")}</h2>
            <p className="mt-2 max-w-xl mx-auto text-sm sm:text-base text-muted-foreground">{t("landing.finalCtaSubtitle")}</p>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              <a
                href="/contact"
                className="btn-glossy relative inline-flex items-center gap-2 rounded-2xl px-6 h-12 text-sm sm:text-base font-semibold overflow-hidden group"
                data-testid="final-cta-primary"
              >
                <span aria-hidden className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/35 to-transparent animate-[shimmer_2.8s_ease-in-out_infinite]" />
                <span className="relative z-10">{t("landing.finalCtaButton")}</span>
              </a>
              <a href="#services" className="inline-flex items-center gap-2 rounded-2xl border border-border bg-background/70 backdrop-blur px-6 h-12 text-sm sm:text-base font-medium hover:bg-accent transition-colors">
                {t("landing.finalCtaSecondary")}
              </a>
            </div>
          </div>
        </Card>
      </motion.div>
    </section>
  );
}
